/*
 * Vesper product page.
 * One ProductController per product root ([data-product-root]) keeps the selected variant and updates
 * forms, URL, gallery, buttons and server-rendered parts ([data-variant-sync]) when it changes.
 * Custom elements:
 *  <variant-picker>        pills / dropdowns / swatches / quantity-break cards per option
 *  <quantity-breaks>       quantity offers ("bundle and save"), optional variant per unit
 *  <product-upsells>       add-on products added with the main product
 *  <bundle-offer>          bundle of products (main offer or own button)
 *  <quantity-gifts>        gifts that unlock with quantity
 *  <sticky-atc>            sticky add to cart bar
 *  <product-custom-field>  line item property inputs with required validation
 *  <clickable-discount>    applies + copies a discount code
 *  <delivery-dates>        delivery estimates computed from today
 *  <size-chart-block>      size chart opener (can move into the variant picker)
 *  <complementary-products> lazy-loaded complementary products
 * Elements that add cart lines expose getCartLines() and carry [data-cart-contributor]; product-form.js reads them.
 */
(() => {
  if (window.VesperProduct) return;

  /* ---------- Helpers ---------- */
  const escapeHtml = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  function formatMoney(cents, format, noTrailingZeros = false) {
    const value = Math.round(Number(cents) || 0);
    const fmt = format || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const number = (precision, thousands = ',', decimal = '.') => {
      const p = noTrailingZeros && value % 100 === 0 ? 0 : precision;
      const [whole, frac] = (value / 100).toFixed(p).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    const map = {
      amount: () => number(2),
      amount_no_decimals: () => number(0),
      amount_with_comma_separator: () => number(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => number(0, '.', ','),
      amount_with_apostrophe_separator: () => number(2, "'", '.'),
      amount_no_decimals_with_space_separator: () => number(0, ' ', ','),
      amount_with_space_separator: () => number(2, ' ', ','),
      amount_with_period_and_space_separator: () => number(2, ' ', '.'),
    };
    return fmt.replace(placeholder, (map[match[1]] || map.amount)());
  }

  function fillTemplate(template, values) {
    if (!template) return '';
    return Object.entries(values).reduce((out, [key, val]) => out.split(`[${key}]`).join(val), template);
  }

  function parseJson(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || el);
    } catch (e) {
      return fallback;
    }
  }

  function sizedImage(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  function cartSectionsTarget() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  // Adds lines to the cart and opens the cart drawer / notification (or goes to checkout).
  async function addLines(lines, { skipCart = false } = {}) {
    const cart = cartSectionsTarget();
    const sections = cart && !skipCart && cart.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : [];
    const res = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || window.cartStrings?.error || 'Error');
    if (skipCart) {
      window.location.href = '/checkout';
      return data;
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vesper-product', cartData: data });
    }
    if (cart && cart.renderContents) {
      cart.classList.remove('is-empty');
      cart.renderContents({ ...(data.items ? data.items[0] : data), sections: data.sections });
    } else {
      window.location.href = window.routes.cart_url;
    }
    return data;
  }

  /* ---------- Controller ---------- */
  const controllers = new WeakMap();

  class ProductController {
    static for(el) {
      const root = el?.closest?.('[data-product-root]');
      if (!root) return null;
      if (!controllers.has(root)) controllers.set(root, new ProductController(root));
      return controllers.get(root);
    }

    constructor(root) {
      this.root = root;
      this.extraRoots = new Set();
      this.sectionId = root.dataset.section;
      const info = root.querySelector('product-info');
      this.originalSection = (info && info.dataset.originalSection) || root.dataset.originalSection || this.sectionId;
      this.url = root.dataset.url;
      this.moneyFormat = root.dataset.moneyFormat;
      this.data = parseJson(root.querySelector('[data-product-json]'), { variants: [] });
      this.variants = this.data.variants || [];
      const idInput = this.form()?.querySelector('[name="id"]');
      const initialId = idInput ? idInput.value : root.dataset.variantId;
      this.variant = this.variants.find((v) => String(v.id) === String(initialId)) || this.variants[0] || null;
      root.addEventListener('change', (e) => {
        if (e.target.matches('[name="quantity"]')) this.emitQuantity();
      });
      root.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-scroll-target]');
        if (!trigger) return;
        e.preventDefault();
        scrollToTarget(trigger.dataset.scrollTarget);
      });
      root.addEventListener('keydown', (e) => {
        const trigger = e.target.closest('[data-scroll-target]');
        if (trigger && (e.key === 'Enter' || e.key === ' ') && e.target === trigger) {
          e.preventDefault();
          scrollToTarget(trigger.dataset.scrollTarget);
        }
      });
      root.querySelectorAll('[data-scroll-target]').forEach((el) => {
        if (!el.querySelector('a[href]')) {
          el.setAttribute('role', 'link');
          el.setAttribute('tabindex', '0');
        }
      });
      requestAnimationFrame(() => {
        this.refreshBlocked();
        this.updateAtcPrice();
      });
    }

    money(cents, noTrailingZeros) {
      return formatMoney(cents, this.moneyFormat, noTrailingZeros);
    }

    get isQuickAdd() {
      return !!this.root.closest('quick-add-modal');
    }

    form() {
      return document.getElementById(`product-form-${this.sectionId}`);
    }

    forms() {
      return [...document.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`)];
    }

    register(el) {
      this.extraRoots.add(el);
    }

    $$(selector) {
      const found = [...this.root.querySelectorAll(selector)];
      this.extraRoots.forEach((r) => {
        if (!this.root.contains(r)) found.push(...r.querySelectorAll(selector));
      });
      return found;
    }

    findVariant(options) {
      return this.variants.find((v) => v.options.every((o, i) => options[i] === undefined || options[i] === null || o === options[i])) || null;
    }

    findExact(options) {
      return this.variants.find((v) => v.options.every((o, i) => o === options[i])) || null;
    }

    variantById(id) {
      return this.variants.find((v) => String(v.id) === String(id)) || null;
    }

    quantity() {
      const form = this.form();
      const field = form && form.elements.namedItem('quantity');
      const value = field ? Number(field.value) : 1;
      return value > 0 ? value : 1;
    }

    emitQuantity() {
      this.root.dispatchEvent(new CustomEvent('vesper:quantity-change', { detail: { quantity: this.quantity() } }));
      this.updateAtcPrice();
    }

    setVariant(variant, source) {
      this.variant = variant;
      if (!variant) {
        this.setUnavailable();
        this.emit(null, source);
        return;
      }
      this.forms().forEach((form) => {
        const input = form.querySelector('[name="id"]');
        if (!input) return;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.updateUrl(variant);
      const gallery = this.root.querySelector('media-gallery');
      const picker = this.root.querySelector('variant-picker');
      const prepend = !(picker && picker.dataset.prepend === 'false');
      if (gallery && gallery.onVariantChange) gallery.onVariantChange(variant, prepend);
      this.updateModalOrder(variant);
      this.updateButtons(variant);
      this.updatePickup(variant);
      this.updateShare(variant);
      this.handleError();
      this.fetchSection(variant);
      this.emit(variant, source);
      this.updateAtcPrice();
    }

    emit(variant, source) {
      this.root.dispatchEvent(new CustomEvent('vesper:variant-change', { detail: { variant, source } }));
    }

    updateUrl(variant) {
      if (this.root.dataset.updateUrl !== 'true' || this.isQuickAdd || !this.url) return;
      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
    }

    updateModalOrder(variant) {
      if (!variant.featured_media) return;
      const content = document.querySelector(`#ProductModal-${this.sectionId} .product-media-modal__content`);
      const item = content && content.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (item) content.prepend(item);
    }

    updateButtons(variant) {
      const strings = window.variantStrings || {};
      this.$$('[data-atc-button]').forEach((btn) => {
        btn.toggleAttribute('disabled', !variant || !variant.available);
        const text = btn.querySelector('[data-atc-text]');
        if (text) text.textContent = !variant ? strings.unavailable : variant.available ? strings.addToCart : strings.soldOut;
      });
    }

    setUnavailable() {
      const strings = window.variantStrings || {};
      this.$$('[data-atc-button]').forEach((btn) => {
        btn.setAttribute('disabled', '');
        const text = btn.querySelector('[data-atc-text]');
        if (text) text.textContent = strings.unavailable || 'Unavailable';
      });
      this.$$('[data-variant-sync]').forEach((el) => {
        if (/^(price|sku|inventory)-/.test(el.dataset.variantSync)) el.classList.add('visibility-hidden');
      });
    }

    updatePickup(variant) {
      const pickup = this.root.querySelector('pickup-availability');
      if (!pickup) return;
      if (variant && variant.available && pickup.fetchAvailability) {
        pickup.fetchAvailability(variant.id);
      } else {
        pickup.removeAttribute('available');
        pickup.innerHTML = '';
      }
    }

    updateShare(variant) {
      const share = document.getElementById(`Share-${this.sectionId}`);
      if (share && share.updateUrl) share.updateUrl(`${window.shopUrl}${this.url}?variant=${variant.id}`);
    }

    handleError(message = false) {
      this.root.querySelectorAll('product-form').forEach((pf) => pf.handleErrorMessage && pf.handleErrorMessage(message));
    }

    async fetchSection(variant) {
      const requested = variant.id;
      this.pending = requested;
      try {
        const res = await fetch(`${this.url}?variant=${requested}&section_id=${this.originalSection}`);
        if (!res.ok) throw new Error(`Section request failed (${res.status})`);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        if (this.pending !== requested) return;
        let synced = 0;
        this.$$('[data-variant-sync]').forEach((el) => {
          const key = el.dataset.variantSync.split(this.sectionId).join(this.originalSection);
          const source = html.querySelector(`[data-variant-sync="${key}"]`);
          if (!source) return;
          synced++;
          el.innerHTML = source.innerHTML;
          el.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
        });
        if (!synced) this.clientPrice(variant);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.originalSection, html, variant } });
        }
      } catch (e) {
        if (this.pending === requested) this.clientPrice(variant);
      }
    }

    // Fallback when the section can't be re-rendered (e.g. a featured product on another page).
    clientPrice(variant) {
      this.$$('[data-variant-sync] .price').forEach((price) => {
        const onSale = variant.compare_at_price > variant.price;
        price.classList.toggle('price--on-sale', onSale);
        price.classList.toggle('price--sold-out', !variant.available);
        price.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale, .price__sale .price-item--last').forEach((el) => {
          el.textContent = this.money(variant.price);
        });
        price.querySelectorAll('.price__sale s, .price__compare s').forEach((el) => {
          el.textContent = onSale ? this.money(variant.compare_at_price) : '';
        });
      });
      this.$$('[data-variant-sync]').forEach((el) => el.classList.remove('visibility-hidden'));
    }

    contributors() {
      return [...this.root.querySelectorAll('[data-cart-contributor]')];
    }

    selectionTotal() {
      let total = null;
      let extra = 0;
      this.contributors().forEach((el) => {
        if (typeof el.displayTotal !== 'function') return;
        const result = el.displayTotal();
        if (!result) return;
        if (result.replaceMain) total = result.amount;
        else extra += result.amount;
      });
      if (total === null) total = (this.variant ? this.variant.price : 0) * this.quantity();
      return total + extra;
    }

    updateAtcPrice() {
      const text = this.money(this.selectionTotal());
      this.$$('[data-atc-price]').forEach((el) => (el.textContent = text));
    }

    fields() {
      return [...this.root.querySelectorAll('product-custom-field[data-required]')];
    }

    refreshBlocked() {
      const invalid = this.fields().filter((f) => !f.isFilled());
      const last = invalid[invalid.length - 1];
      const message = last ? last.dataset.atcMessage || '' : '';
      const stickyMessage = invalid.filter((f) => f.dataset.sticky === 'true').pop()?.dataset.atcMessage || '';
      const blocked = invalid.length > 0;
      this.$$('[data-atc-button]').forEach((btn) => setBlocked(btn, blocked, message));
      this.$$('[data-sticky-button]').forEach((btn) => setBlocked(btn, !!stickyMessage, stickyMessage));
      this.$$('.product-form__express').forEach((el) => el.classList.toggle('is-blocked', blocked));
    }

    // Returns false (and shows errors) when a required field is empty.
    validate(showErrors = true) {
      const invalid = this.fields().filter((f) => !f.isFilled());
      if (!invalid.length) return true;
      if (showErrors) {
        invalid.forEach((f) => f.showError(true));
        invalid[0].focusField();
      }
      return false;
    }
  }

  function setBlocked(btn, blocked, message) {
    btn.classList.toggle('is-blocked', blocked && !!message);
    const text = btn.querySelector('[data-blocked-text]');
    if (text) text.textContent = blocked ? message : '';
  }

  function scrollToTarget(value) {
    if (!value) return;
    const id = value.replace(/^#/, '');
    const target =
      document.getElementById(id) ||
      document.getElementById(`shopify-section-${id}`) ||
      (() => {
        try {
          return document.querySelector(value);
        } catch (e) {
          return null;
        }
      })();
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      if (!this.controller) return;
      this.addEventListener('change', (e) => this.onChange(e));
      this.controller.root.addEventListener('vesper:variant-change', (e) => {
        if (e.detail.source !== this && e.detail.variant) this.syncFrom(e.detail.variant);
      });
      this.updateAvailability();
    }

    optionGroups() {
      return [...this.querySelectorAll('[data-option-position]')];
    }

    selectedOptions() {
      return this.optionGroups().map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    onChange(event) {
      if (!event.target.closest('[data-option-position]')) return;
      const options = this.selectedOptions();
      const variant = this.controller.findExact(options);
      this.updateLabels(options);
      this.updateAvailability();
      this.updateBreakPrices(options);
      this.controller.setVariant(variant, this);
    }

    syncFrom(variant) {
      this.optionGroups().forEach((group, i) => {
        const value = variant.options[i];
        const select = group.querySelector('select');
        if (select) select.value = value;
        group.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = r.value === value));
      });
      this.updateLabels(variant.options);
      this.updateAvailability();
      this.updateBreakPrices(variant.options);
    }

    updateLabels(options) {
      this.optionGroups().forEach((group, i) => {
        group.querySelectorAll('[data-selected-value]').forEach((el) => (el.textContent = options[i] ?? ''));
        group.querySelectorAll('.qty-break').forEach((card) => {
          card.classList.toggle('is-selected', card.querySelector('input')?.checked);
        });
      });
    }

    updateAvailability() {
      const options = this.selectedOptions();
      const variants = this.controller.variants;
      const strings = window.variantStrings || {};
      this.optionGroups().forEach((group, index) => {
        const available = new Set(
          variants
            .filter((v) => v.available && options.slice(0, index).every((o, i) => v.options[i] === o))
            .map((v) => v.options[index])
        );
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const ok = available.has(input.value);
          input.classList.toggle('disabled', !ok);
          input.closest('.qty-break')?.classList.toggle('is-unavailable', !ok);
          const label = group.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (label) label.toggleAttribute('data-unavailable', !ok);
        });
        group.querySelectorAll('select option').forEach((opt) => {
          if (!opt.dataset.label) opt.dataset.label = opt.textContent.trim();
          const ok = available.has(opt.value);
          opt.textContent = ok ? opt.dataset.label : (strings.unavailable_with_option || '[value] - Unavailable').replace('[value]', opt.dataset.label);
        });
      });
    }

    // Quantity-break cards on variant options show the price of the matching variant.
    updateBreakPrices(options) {
      const controller = this.controller;
      this.optionGroups().forEach((group, index) => {
        const cards = group.querySelectorAll('.qty-break[data-value]');
        if (!cards.length) return;
        cards.forEach((card) => {
          const target = options.slice();
          target[index] = card.dataset.value;
          const v = controller.findExact(target) || controller.variants.find((x) => x.options[index] === card.dataset.value);
          if (!v) return;
          const compare = Math.max(v.compare_at_price || 0, v.price);
          const saved = compare - v.price;
          const values = {
            name: escapeHtml(card.dataset.value),
            price: controller.money(v.price),
            compare_price: controller.money(compare),
            amount_saved_rounded: controller.money(Math.ceil(saved / 100) * 100, true),
            amount_saved: controller.money(saved),
          };
          renderCardTexts(card, values);
          card.querySelector('[data-qb-field="compare"]')?.classList.toggle('hidden', compare <= v.price);
        });
      });
    }
  }

  function renderCardTexts(card, values) {
    const texts = parseJson(card.dataset.texts, null);
    if (!texts) return;
    card.querySelectorAll('[data-qb-field]').forEach((el) => {
      const tpl = texts[el.dataset.qbField];
      if (tpl !== undefined) el.innerHTML = fillTemplate(tpl, values);
    });
  }

  /* ---------- Quantity offers ---------- */
  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      if (!this.controller) return;
      this.input = this.querySelector('[data-breaks-quantity]');
      this.addEventListener('change', (e) => {
        if (e.target.matches('.qty-break__input')) this.select(e.target.closest('.qty-break'));
        else if (e.target.matches('[data-unit-option]')) {
          this.syncUnit(e.target.closest('.qty-break__picker'));
          this.render();
          this.controller.updateAtcPrice();
        } else if (e.target.matches('[data-unit-variant]')) {
          this.render();
          this.controller.updateAtcPrice();
        }
      });
      this.addEventListener('click', (e) => {
        const card = e.target.closest('.qty-break');
        if (!card || card.classList.contains('is-selected') || !e.target.closest('.qty-break__pickers')) return;
        const input = card.querySelector('.qty-break__input');
        input.checked = true;
        this.select(card);
      });
      this.controller.root.addEventListener('vesper:variant-change', (e) => {
        const v = e.detail.variant;
        if (!v) return;
        this.querySelectorAll('.qty-break__picker').forEach((row) => {
          const field = row.querySelector('[data-unit-variant]');
          if (field) field.value = v.id;
          row.querySelectorAll('[data-unit-option]').forEach((s) => (s.value = v.options[Number(s.dataset.unitOption)]));
          row.classList.remove('is-unavailable');
        });
        this.render();
      });
      const selected = this.selected;
      if (selected) this.select(selected);
    }

    // A unit's option selects changed: store the matching variant in the unit's hidden field.
    syncUnit(row) {
      if (!row) return;
      const options = [...row.querySelectorAll('[data-unit-option]')].map((s) => s.value);
      const variant = this.controller.findExact(options);
      const field = row.querySelector('[data-unit-variant]');
      if (variant && field) field.value = variant.id;
      row.classList.toggle('is-unavailable', !variant || !variant.available);
    }

    get selected() {
      const input = this.querySelector('.qty-break__input:checked');
      return input ? input.closest('.qty-break') : null;
    }

    select(card) {
      this.querySelectorAll('.qty-break').forEach((c) => c.classList.toggle('is-selected', c === card));
      if (this.input) this.input.value = card ? card.dataset.quantity : 1;
      this.controller.emitQuantity();
    }

    unitVariants(card) {
      return [...card.querySelectorAll('[data-unit-variant]')].map((s) => this.controller.variantById(s.value)).filter(Boolean);
    }

    priceFor(card) {
      const quantity = Number(card.dataset.quantity) || 1;
      const pct = Number(card.dataset.pct) || 0;
      const fixed = Number(card.dataset.fixed) || 0;
      const base = this.controller.variant;
      let units = this.unitVariants(card);
      if (!units.length) units = Array(quantity).fill(base);
      let total = 0;
      let compare = 0;
      units.forEach((u) => {
        if (!u) return;
        total += u.price;
        compare += card.dataset.basis === 'price' ? u.price : Math.max(u.compare_at_price || 0, u.price);
      });
      const price = Math.max(0, Math.round((total * (100 - pct)) / 100 - fixed));
      return { quantity, price, compare };
    }

    render() {
      if (this.dataset.updatePrices !== 'true') return;
      const money = (c, t) => this.controller.money(c, t);
      this.querySelectorAll('.qty-break').forEach((card) => {
        const { quantity, price, compare } = this.priceFor(card);
        const saved = Math.max(0, compare - price);
        renderCardTexts(card, {
          quantity,
          price_each: money(Math.round(price / quantity), true),
          compare_price_each: money(Math.round(compare / quantity), true),
          price: money(price, true),
          compare_price: money(compare, true),
          amount_saved_rounded: money(Math.ceil(saved / 100) * 100, true),
          amount_saved: money(saved, true),
        });
        card.querySelector('[data-qb-field="compare"]')?.classList.toggle('hidden', compare <= price);
      });
    }

    displayTotal() {
      const card = this.selected;
      return card ? { replaceMain: true, amount: this.priceFor(card).price } : null;
    }

    getCartLines() {
      const card = this.selected;
      if (!card) return null;
      const units = [...card.querySelectorAll('[data-unit-variant]')].map((s) => s.value);
      if (!units.length) return null;
      const counts = units.reduce((acc, id) => ((acc[id] = (acc[id] || 0) + 1), acc), {});
      return { replaceMain: true, lines: Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity })) };
    }
  }

  /* ---------- Add-on products ---------- */
  class ProductUpsells extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      this.querySelectorAll('[data-upsell]').forEach((item) => {
        const checkbox = item.querySelector('.upsell__checkbox');
        item._variants = parseJson(item.querySelector('[data-upsell-variants]'), []);
        checkbox.addEventListener('change', () => {
          item.classList.toggle('is-selected', checkbox.checked);
          this.controller?.updateAtcPrice();
        });
        if (item.classList.contains('upsell--toggle-container')) {
          item.addEventListener('click', (e) => {
            if (e.target.closest('a, select, input, label, button') || checkbox.disabled) return;
            checkbox.checked = !checkbox.checked;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
        item.querySelector('select[data-upsell-variant]')?.addEventListener('change', () => this.updateItem(item));
      });
    }

    itemPrice(item) {
      const id = item.querySelector('[data-upsell-variant]')?.value;
      const v = item._variants.find((x) => String(x.id) === String(id)) || item._variants[0];
      if (!v) return null;
      const pct = Number(item.dataset.pct) || 0;
      const fixed = Number(item.dataset.fixed) || 0;
      const price = Math.max(0, Math.round((v.price * (100 - pct)) / 100 - fixed));
      return { v, price, compare: Math.max(v.compare_at_price || 0, v.price) };
    }

    updateItem(item) {
      const info = this.itemPrice(item);
      if (!info) return;
      const checkbox = item.querySelector('.upsell__checkbox');
      if (!info.v.available) {
        checkbox.checked = false;
        item.classList.remove('is-selected');
      }
      checkbox.disabled = !info.v.available;
      if (this.dataset.updatePrices === 'true' && this.controller) {
        item.querySelectorAll('[data-price]').forEach((el) => (el.textContent = this.controller.money(info.price)));
        item.querySelectorAll('[data-compare]').forEach((el) => {
          el.textContent = this.controller.money(info.compare);
          el.classList.toggle('hidden', info.compare <= info.price);
        });
      }
      const img = item.querySelector('[data-upsell-image]');
      if (img && info.v.image) {
        img.removeAttribute('srcset');
        img.src = info.v.image;
      }
      this.controller?.updateAtcPrice();
    }

    selectedItems() {
      return [...this.querySelectorAll('[data-upsell]')].filter((item) => item.querySelector('.upsell__checkbox:checked'));
    }

    displayTotal() {
      const amount = this.selectedItems().reduce((sum, item) => sum + (this.itemPrice(item)?.price || 0), 0);
      return { replaceMain: false, amount };
    }

    getCartLines() {
      const lines = this.selectedItems()
        .map((item) => Number(item.querySelector('[data-upsell-variant]')?.value))
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1 }));
      return lines.length ? { replaceMain: false, lines } : null;
    }
  }

  /* ---------- Bundle offer ---------- */
  class BundleOffer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      this.items = [...this.querySelectorAll('[data-bundle-item]')];
      this.items.forEach((item) => {
        item._variants = parseJson(item.querySelector('[data-bundle-variants]'), []);
        item.querySelector('select[data-bundle-variant]')?.addEventListener('change', () => this.update());
      });
      this.querySelector('[data-bundle-add]')?.addEventListener('click', (e) => this.addSeparately(e.currentTarget));
    }

    money(cents) {
      return this.controller ? this.controller.money(cents) : formatMoney(cents, document.querySelector('[data-money-format]')?.dataset.moneyFormat);
    }

    itemInfo(item) {
      const id = item.querySelector('[data-bundle-variant]')?.value;
      const v = item._variants.find((x) => String(x.id) === String(id)) || item._variants[0];
      if (!v) return null;
      const pct = Number(item.dataset.pct) || 0;
      const fixed = Number(item.dataset.fixed) || 0;
      return { v, price: Math.max(0, Math.round((v.price * (100 - pct)) / 100 - fixed)), compare: Math.max(v.compare_at_price || 0, v.price) };
    }

    totals() {
      let price = 0;
      let compare = 0;
      this.items.forEach((item) => {
        const info = this.itemInfo(item);
        if (!info) return;
        price += info.price;
        compare += info.compare;
      });
      const pct = Number(this.dataset.pct) || 0;
      const fixed = Number(this.dataset.fixed) || 0;
      return { price: Math.max(0, Math.round((price * (100 - pct)) / 100 - fixed)), compare };
    }

    update() {
      const live = this.dataset.updatePrices === 'true';
      this.items.forEach((item) => {
        const info = this.itemInfo(item);
        if (!info) return;
        const img = item.querySelector('[data-bundle-image]');
        if (img && info.v.image) {
          img.removeAttribute('srcset');
          img.src = info.v.image;
        }
        if (!live) return;
        const free = info.price === 0 && this.dataset.freeMode === 'text';
        item.querySelectorAll('[data-price]').forEach((el) => (el.textContent = free ? this.dataset.freeText : this.money(info.price)));
        item.querySelectorAll('[data-compare]').forEach((el) => {
          el.textContent = this.money(info.compare);
          el.classList.toggle('hidden', info.compare <= info.price);
        });
      });
      if (live) {
        const t = this.totals();
        this.querySelectorAll('[data-total]').forEach((el) => (el.textContent = this.money(t.price)));
        this.querySelectorAll('[data-total-compare]').forEach((el) => {
          el.textContent = this.money(t.compare);
          el.classList.toggle('hidden', t.compare <= t.price);
        });
      }
      this.controller?.updateAtcPrice();
    }

    lines() {
      return this.items.map((item) => Number(item.querySelector('[data-bundle-variant]')?.value)).filter(Boolean).map((id) => ({ id, quantity: 1 }));
    }

    displayTotal() {
      if (this.dataset.type !== 'main' || !this.items.length) return null;
      return { replaceMain: true, amount: this.totals().price };
    }

    getCartLines() {
      if (this.dataset.type !== 'main') return null;
      const lines = this.lines();
      return lines.length ? { replaceMain: true, lines } : null;
    }

    async addSeparately(button) {
      const error = this.querySelector('.bundle__error');
      if (error) error.hidden = true;
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      button.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
      try {
        await addLines(this.lines());
      } catch (err) {
        if (error) {
          error.textContent = err.message;
          error.hidden = false;
        }
      } finally {
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
      }
    }
  }

  /* ---------- Gifts by quantity ---------- */
  class QuantityGifts extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      if (!this.controller) return;
      this.controller.root.addEventListener('vesper:quantity-change', () => this.update());
      this.update();
    }

    currentQuantity() {
      const breaks = this.controller.root.querySelector('quantity-breaks');
      const card = breaks && breaks.selected;
      if (card) return Number(card.dataset.quantity) || 1;
      return this.controller.quantity();
    }

    update() {
      const qty = this.currentQuantity();
      this.querySelectorAll('[data-gift]').forEach((gift) => {
        const unlocked = qty >= (Number(gift.dataset.minQuantity) || 1);
        if (unlocked && !gift.classList.contains('is-unlocked')) {
          gift.classList.add('is-unlocking');
          setTimeout(() => gift.classList.remove('is-unlocking'), 700);
        }
        gift.classList.toggle('is-unlocked', unlocked);
      });
    }

    getCartLines() {
      const lines = [...this.querySelectorAll('[data-gift].is-unlocked[data-variant-id]')].map((g) => ({ id: Number(g.dataset.variantId), quantity: 1 }));
      return lines.length ? { replaceMain: false, lines } : null;
    }
  }

  /* ---------- Sticky add to cart ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      if (!this.controller || this.controller.isQuickAdd) return;
      const controller = this.controller;
      controller.register(this);
      this.dataset.section = controller.sectionId;
      this.button = this.querySelector('[data-sticky-button]');
      this.target = this.dataset.target;
      this.button.addEventListener('click', () => this.onClick());
      this.querySelector('[data-sticky-variant]')?.addEventListener('change', (e) => controller.setVariant(controller.variantById(e.target.value), this));
      this.querySelectorAll('[data-sticky-option]').forEach((select) =>
        select.addEventListener('change', () => {
          const options = [...this.querySelectorAll('[data-sticky-option]')].map((s) => s.value);
          controller.setVariant(controller.findExact(options), this);
        })
      );
      controller.root.addEventListener('vesper:variant-change', (e) => this.sync(e.detail.variant, e.detail.source));
      document.addEventListener('vesper:product-form', (e) => {
        if (e.detail.formId !== this.dataset.form) return;
        this.button.classList.toggle('loading', e.detail.loading);
        this.button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !e.detail.loading);
      });
      // Live outside the product column so the fixed bar is never clipped by sticky/transformed parents.
      document.body.appendChild(this);
      this.observe();
      this.resizeObserver = new ResizeObserver(() => document.body.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`));
      this.resizeObserver.observe(this);
      document.body.classList.add('has-sticky-atc');
    }

    observe() {
      if (this.dataset.display === 'always') {
        this.show(true);
        return;
      }
      const anchor = document.getElementById(`ProductSubmitButton-${this.controller.sectionId}`) || this.controller.form() || this.controller.root;
      // Scroll check (not IntersectionObserver) so jumps past the button (anchor links) are caught too.
      let frame = null;
      const check = () => {
        frame = null;
        if (anchor.isConnected) this.show(anchor.getBoundingClientRect().bottom < 0);
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(check);
      };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      check();
    }

    show(visible) {
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
    }

    onClick() {
      if (this.target) {
        scrollToTarget(this.target);
        return;
      }
      const controller = this.controller;
      if (!controller.variant || !controller.variant.available) return;
      if (!controller.validate(true)) return;
      const form = controller.form();
      if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
    }

    sync(variant, source) {
      if (source !== this && variant) {
        const select = this.querySelector('[data-sticky-variant]');
        if (select) select.value = variant.id;
        this.querySelectorAll('[data-sticky-option]').forEach((s) => (s.value = variant.options[Number(s.dataset.stickyOption)]));
      }
      const img = this.querySelector('[data-sticky-image]');
      if (img && variant && variant.featured_image) {
        img.removeAttribute('srcset');
        img.src = sizedImage(variant.featured_image.src, 160);
      }
      if (!this.target) {
        const strings = window.variantStrings || {};
        const text = this.querySelector('[data-sticky-text]');
        const ok = variant && variant.available;
        this.button.toggleAttribute('disabled', !ok);
        if (text) text.textContent = ok ? text.dataset.label : variant ? strings.soldOut : strings.unavailable;
      }
    }
  }

  /* ---------- Custom fields ---------- */
  class ProductCustomField extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = ProductController.for(this);
      this.error = this.querySelector('.custom-field__error');
      this.counter = this.querySelector('[data-char-count]');
      const onInput = () => {
        if (this.counter) this.counter.textContent = this.querySelector('input')?.value.length || 0;
        if (this.hasAttribute('data-required')) this.showError(!this.isFilled());
        this.controller?.refreshBlocked();
      };
      this.addEventListener('input', onInput);
      this.addEventListener('change', onInput);
    }

    isFilled() {
      const radios = this.querySelectorAll('input[type="radio"]');
      if (radios.length) return [...radios].some((r) => r.checked);
      const field = this.querySelector('input, select, textarea');
      return !!field && String(field.value || '').trim() !== '';
    }

    showError(show) {
      if (this.error) this.error.hidden = !show;
      this.classList.toggle('has-error', show);
      this.querySelectorAll('input, select, textarea').forEach((f) => {
        f.setAttribute('aria-invalid', show ? 'true' : 'false');
        if (this.error) f.setAttribute('aria-describedby', this.error.id);
      });
    }

    focusField() {
      const field = this.querySelector('input, select, textarea');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (field) setTimeout(() => field.focus({ preventScroll: true }), 300);
    }
  }

  /* ---------- Discount code button ---------- */
  class ClickableDiscount extends HTMLElement {
    connectedCallback() {
      this.code = (this.dataset.code || '').trim();
      this.key = `vesper-discount:${this.code.toLowerCase()}`;
      this.button = this.querySelector('button');
      let applied = false;
      try {
        applied = localStorage.getItem(this.key) === '1';
      } catch (e) {}
      this.setApplied(applied);
      this.button.addEventListener('click', () => this.apply());
    }

    setApplied(on) {
      this.classList.toggle('is-applied', on);
      this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
    }

    async apply() {
      if (!this.code) return;
      this.setApplied(true);
      try {
        await navigator.clipboard.writeText(this.code);
      } catch (e) {}
      try {
        localStorage.setItem(this.key, '1');
      } catch (e) {}
      try {
        await fetch(`/discount/${encodeURIComponent(this.code)}?redirect=${encodeURIComponent('/cart.js')}`, { credentials: 'same-origin' });
      } catch (e) {}
    }
  }

  /* ---------- Delivery dates ---------- */
  function ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  function formatDate(date, format, days, months) {
    const day = days[(date.getDay() + 6) % 7] || '';
    const month = months[date.getMonth()] || '';
    const d = date.getDate();
    const dd = String(d).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    switch (format) {
      case 'day_dd_mm':
        return `${day}, ${d}. ${month}`;
      case 'mm_dd':
        return `${month} ${ordinal(d)}`;
      case 'dd_mm':
        return `${d}. ${month}`;
      case 'dd_mm_no_dot':
        return `${d} ${month}`;
      case 'day_dd_mm_numeric':
        return `${day}, ${dd}. ${mm}.`;
      case 'dd_mm_numeric':
        return `${dd}. ${mm}.`;
      default:
        return `${day}, ${month} ${ordinal(d)}`;
    }
  }

  class DeliveryDates extends HTMLElement {
    connectedCallback() {
      const split = (s, fallback) => {
        const list = (s || '').split(',').map((x) => x.trim()).filter(Boolean);
        return list.length ? list : fallback;
      };
      const days = split(this.dataset.days, ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']);
      const months = split(this.dataset.months, ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']);
      const ranges = parseJson(this.dataset.ranges, []);
      const format = this.dataset.format;
      const today = new Date();
      const plus = (n) => {
        const d = new Date(today);
        d.setDate(d.getDate() + (Number(n) || 0));
        return d;
      };
      this.querySelectorAll('[data-date-scope]').forEach((scope) => {
        const range = ranges[Number(scope.dataset.dateScope)] || { start: 0, end: 0 };
        scope.querySelectorAll('[data-date="start"]').forEach((el) => (el.textContent = formatDate(plus(range.start), format, days, months)));
        scope.querySelectorAll('[data-date="end"]').forEach((el) => (el.textContent = formatDate(plus(range.end), format, days, months)));
      });
    }
  }

  /* ---------- Size chart ---------- */
  class SizeChartBlock extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.opener = this.querySelector('modal-opener');
      this.modalId = this.opener?.dataset.modal;
      requestAnimationFrame(() => this.place());
      if (this.hasAttribute('data-test-mode')) setTimeout(() => this.open(), 400);
      if (Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (e) => e.target === this && this.open());
        document.addEventListener('shopify:block:deselect', (e) => e.target === this && this.close());
      }
    }

    modal() {
      return this.modalId ? document.querySelector(this.modalId) : null;
    }

    open() {
      const modal = this.modal();
      if (modal && !modal.hasAttribute('open')) modal.show(this.opener.querySelector('button'));
    }

    close() {
      const modal = this.modal();
      if (modal && modal.hasAttribute('open')) modal.hide();
    }

    place() {
      const match = (this.dataset.append || '').match(/^option_(\d)_(label|under)_container$/);
      if (!match || !this.opener) return;
      const root = this.closest('[data-product-root]') || document;
      const group = root.querySelector(`variant-picker [data-option-position="${match[1]}"]`);
      const slot = group && group.querySelector(match[2] === 'label' ? '[data-append-label]' : '[data-append-under]');
      if (!slot) return;
      this.opener.classList.add('size-chart__opener--appended');
      this.opener.style.setProperty('--size-btn-size', getComputedStyle(this).getPropertyValue('--size-btn-size'));
      slot.appendChild(this.opener);
      this.classList.add('size-chart--moved');
    }
  }

  /* ---------- Complementary products ---------- */
  class ComplementaryProducts extends HTMLElement {
    connectedCallback() {
      if (this.observer || !this.dataset.url) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    async load() {
      try {
        const res = await fetch(this.dataset.url);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const source = html.getElementById(this.id);
        if (source && source.querySelector('aside')) {
          this.innerHTML = source.innerHTML;
          window.Vesper?.init?.(this);
        } else if (!Shopify?.designMode) {
          this.remove();
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };
  define('variant-picker', VariantPicker);
  define('quantity-breaks', QuantityBreaks);
  define('product-upsells', ProductUpsells);
  define('bundle-offer', BundleOffer);
  define('quantity-gifts', QuantityGifts);
  define('sticky-atc', StickyAtc);
  define('product-custom-field', ProductCustomField);
  define('clickable-discount', ClickableDiscount);
  define('delivery-dates', DeliveryDates);
  define('size-chart-block', SizeChartBlock);
  define('complementary-products', ComplementaryProducts);

  // Theme editor: remove modals and sticky bars that were moved to <body> when their section reloads.
  document.addEventListener('shopify:section:unload', (e) => {
    const id = e.detail && e.detail.sectionId;
    if (!id) return;
    document.querySelectorAll(`body > [data-section="${id}"]`).forEach((el) => el.remove());
    document.body.classList.toggle('has-sticky-atc', !!document.querySelector('sticky-atc'));
  });

  window.VesperProduct = {
    controllerFor: (el) => ProductController.for(el),
    formatMoney,
    addLines,
  };
})();
